import { useState, type KeyboardEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, Save, X, Plus, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { updateTouchAngle } from '@/lib/edge-functions'

// One row of touch_angles — the per-day "angle lock" Carlos + the generator read
// to keep each touch on a DIFFERENT Yuno value prop across the 9-day cadence.
export interface TouchAngleRow {
  id: string
  org_id: string
  day_offset: number
  label: string
  capability_keywords: string[]
  forbidden_capabilities: string[]
  primary_numbers: string[]
  primary_peers: string[]
  hook_pattern: string | null
  cta_pattern: string | null
  // Provenance of the EFFECTIVE row this card renders: `null` = inherited from
  // the org-global (cadence_id IS NULL) legacy row; a string = a row already
  // scoped to this cadence. `undefined` = caller didn't thread provenance, in
  // which case we skip the provenance badge. Editing an inherited row silently
  // forks a cadence-scoped copy on save.
  cadence_id?: string | null
}

interface EditState {
  label: string
  capability_keywords: string[]
  forbidden_capabilities: string[]
  primary_numbers: string[]
  primary_peers: string[]
  hook_pattern: string
  cta_pattern: string
}

function toEdit(row: TouchAngleRow): EditState {
  return {
    label: row.label,
    capability_keywords: [...row.capability_keywords],
    forbidden_capabilities: [...row.forbidden_capabilities],
    primary_numbers: [...row.primary_numbers],
    primary_peers: [...row.primary_peers],
    hook_pattern: row.hook_pattern ?? '',
    cta_pattern: row.cta_pattern ?? '',
  }
}

// Stable serialization for dirty-checking (order matters — reordering counts as a change).
function serialize(s: EditState): string {
  return JSON.stringify(s)
}

// ── Chip list: add via Enter or the + button, remove via the × on each chip ──
function ChipListField({
  label,
  hint,
  values,
  placeholder,
  tone,
  onChange,
}: {
  label: string
  hint: string
  values: string[]
  placeholder: string
  tone: 'allow' | 'forbid' | 'neutral'
  onChange: (next: string[]) => void
}) {
  const [draft, setDraft] = useState('')

  const add = () => {
    const v = draft.trim()
    if (!v) return
    if (values.includes(v)) {
      setDraft('')
      return
    }
    onChange([...values, v])
    setDraft('')
  }

  const remove = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx))
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      add()
    } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
      // Backspace on an empty input removes the last chip (common chip-UX).
      remove(values.length - 1)
    }
  }

  const chipClass =
    tone === 'forbid'
      ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900'
      : tone === 'allow'
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900'
        : 'bg-muted text-foreground border-border'

  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{label}</Label>
      <div className="rounded-md border p-2 space-y-2">
        {values.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {values.map((v, i) => (
              <span
                key={`${v}-${i}`}
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs ${chipClass}`}
              >
                <span className="break-words">{v}</span>
                <button
                  type="button"
                  aria-label={`Quitar ${v}`}
                  onClick={() => remove(i)}
                  className="opacity-60 hover:opacity-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="h-8 text-xs"
          />
          <Button type="button" size="sm" variant="outline" className="h-8 shrink-0" onClick={add} disabled={!draft.trim()}>
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground leading-tight">{hint}</p>
    </div>
  )
}

function TouchAngleCard({
  row,
  orgId,
  cadenceId,
  onSaved,
}: {
  row: TouchAngleRow
  orgId: string
  cadenceId?: string | null
  onSaved: () => void
}) {
  const [edit, setEdit] = useState<EditState>(() => toEdit(row))

  const dirty = serialize(edit) !== serialize(toEdit(row))

  // AMARILLO (herencia): is this EFFECTIVE row inherited from the org-global
  // legacy config, or already scoped to this cadence? Editing an inherited row
  // silently forks a cadence-scoped copy on save. `undefined` = caller didn't
  // thread provenance → hide the badge rather than guess.
  const inherited = row.cadence_id === null
  const showProvenance = row.cadence_id !== undefined

  const save = useMutation({
    mutationFn: async () => {
      if (!edit.label.trim()) {
        throw new Error('El nombre del ángulo (label) es obligatorio')
      }
      return updateTouchAngle({
        orgId,
        cadence_id: cadenceId ?? null,
        day_offset: row.day_offset,
        label: edit.label.trim(),
        capability_keywords: edit.capability_keywords,
        forbidden_capabilities: edit.forbidden_capabilities,
        primary_numbers: edit.primary_numbers,
        primary_peers: edit.primary_peers,
        hook_pattern: edit.hook_pattern.trim() || null,
        cta_pattern: edit.cta_pattern.trim() || null,
      })
    },
    onSuccess: () => {
      toast.success(`Ángulo guardado — Día ${row.day_offset}`)
      onSaved()
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'No se pudo guardar el ángulo')
    },
  })

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="outline" className="shrink-0 font-mono text-xs">
            Día {row.day_offset}
          </Badge>
          <CardTitle className="text-sm min-w-0 flex-1 truncate">{edit.label || `Día ${row.day_offset}`}</CardTitle>
          {showProvenance && (
            <Badge variant={inherited ? 'secondary' : 'outline'} className="shrink-0 text-[10px]">
              {inherited ? 'Heredado (org-global)' : 'Propio de esta cadencia'}
            </Badge>
          )}
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Guardar
          </Button>
        </div>
        {showProvenance && inherited && (
          <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] leading-tight text-amber-800 flex items-start gap-1.5 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
            <Info className="h-3.5 w-3.5 mt-px shrink-0" />
            <span>Al guardar crearás una versión específica para esta cadencia (hoy hereda la configuración org-global).</span>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Angle name */}
        <div className="space-y-1">
          <Label className="text-xs font-medium">Nombre del ángulo</Label>
          <Input
            value={edit.label}
            onChange={(e) => setEdit((s) => ({ ...s, label: e.target.value }))}
            placeholder="Ej. Smart Routing → Approval Rate Uplift"
            className="h-8 text-sm"
          />
          <p className="text-[11px] text-muted-foreground leading-tight">
            El nombre de la propuesta de valor que ESTE touch debe cubrir (diferente a la de los demás touches).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ChipListField
            label="Capacidades a usar (value-props)"
            hint="Frases exactas que el mensaje puede usar para este touch. La IA elige UNA verbatim."
            values={edit.capability_keywords}
            placeholder="Ej. smart routing"
            tone="allow"
            onChange={(next) => setEdit((s) => ({ ...s, capability_keywords: next }))}
          />
          <ChipListField
            label="Capacidades a evitar (reservadas para otros touches)"
            hint="Value-props que pertenecen a OTROS días. El mensaje NO debe mencionarlas aquí, para no repetir el pitch."
            values={edit.forbidden_capabilities}
            placeholder="Ej. time-to-market"
            tone="forbid"
            onChange={(next) => setEdit((s) => ({ ...s, forbidden_capabilities: next }))}
          />
          <ChipListField
            label="Números defendibles"
            hint="Métricas/beneficios que se pueden citar en este touch. La IA elige UNO."
            values={edit.primary_numbers}
            placeholder="Ej. +5-12pp auth lift offshore→local"
            tone="neutral"
            onChange={(next) => setEdit((s) => ({ ...s, primary_numbers: next }))}
          />
          <ChipListField
            label="Peers verificados (clientes Yuno)"
            hint="Clientes verificados que encajan con ESTE ángulo. La IA elige UNO."
            values={edit.primary_peers}
            placeholder="Ej. inDrive (single API across 47 countries)"
            tone="neutral"
            onChange={(next) => setEdit((s) => ({ ...s, primary_peers: next }))}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-xs font-medium">Patrón de apertura (hook)</Label>
            <Textarea
              value={edit.hook_pattern}
              onChange={(e) => setEdit((s) => ({ ...s, hook_pattern: e.target.value }))}
              placeholder="Qué tipo de apertura usa este touch…"
              className="min-h-[80px] text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium">Patrón de cierre (CTA)</Label>
            <Textarea
              value={edit.cta_pattern}
              onChange={(e) => setEdit((s) => ({ ...s, cta_pattern: e.target.value }))}
              placeholder="Qué tipo de CTA cierra este touch…"
              className="min-h-[80px] text-xs"
            />
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground leading-tight flex items-start gap-1">
          <Info className="h-3 w-3 mt-0.5 shrink-0" />
          Estos valores se inyectan como el bloque "ANGLE LOCK" del mensaje de este día y se usan para que
          Carlos detecte si el touch repite el ángulo de otro. Si borras una fila desde la base, la generación
          cae al valor por defecto del código.
        </p>
      </CardContent>
    </Card>
  )
}

export function TouchAngleEditor({
  rows,
  orgId,
  cadenceId,
}: {
  rows: TouchAngleRow[]
  orgId: string
  cadenceId?: string | null
}) {
  const queryClient = useQueryClient()
  const onSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['touch-angles', orgId] })
    // The effective angle Steps + the PromptDrawer surface is resolved by
    // cadence-flow-metrics (edge fn). Saving an angle here changes that effective
    // value, so invalidate it too or Steps/drawer show the stale angle until
    // staleTime (60s). Prefix-match invalidates every variant of the key
    // ['cadence-flow-metrics', orgId, cadenceId, daysWindow].
    queryClient.invalidateQueries({ queryKey: ['cadence-flow-metrics'] })
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
        Esta organización aún no tiene ángulos por touch configurados. Se siembran con la
        cadencia de outreach (migración 170); si esperabas ver filas aquí, revisa que la
        migración se haya aplicado para tu organización.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {rows.map((row) => (
        <TouchAngleCard key={row.id} row={row} orgId={orgId} cadenceId={cadenceId} onSaved={onSaved} />
      ))}
    </div>
  )
}
